import { Button, Dialog, Divider } from '@wonderschool/common-base-ui';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { usePrimaryLocation } from '../../../hooks/useLocations';
import { AttendanceType, KINDERSYSTEMS_REPORTS_STATUS } from '../../../reports/attendance/types';
import { KinderConnectAttendanceEventStatus } from './KinderConnectAttendanceEventStatus';

dayjs.extend(utc);
dayjs.extend(timezone);

interface TransferDetailsDialogProps {
  isOpen: boolean;
  isTransferringAttendance: boolean;
  onClose: () => void;
  setIsOpen: (isOpen: boolean) => void;
  handleTransferAttendance: () => void;
  attendance?: AttendanceType;
}

export const TransferDetailsDialog: React.FC<TransferDetailsDialogProps> = ({
  isOpen,
  setIsOpen,
  isTransferringAttendance,
  onClose,
  handleTransferAttendance,
  attendance,
}) => {
  const { t } = useTranslation();
  const { timezone } = usePrimaryLocation();
  const [transferredDate, setTransferredDate] = useState<string | null>(null);
  const [lastTransferredDate, setLastTransferredDate] = useState<string | null>(null);

  const missingAutomatedCheckOutCorrection = attendance?.missingAutomatedCheckOutCorrection;
  const kinderSystemsData = attendance?.kinderSystemsTransferred;

  const getLastTransferredDate = useCallback(() => {
    if (kinderSystemsData?.transferHistory) {
      const lastTransferred = kinderSystemsData.transferHistory
        .filter((history: any) => history.status === KINDERSYSTEMS_REPORTS_STATUS.TRANSFERRED)
        .sort((a: any, b: any) => b.transferredAt.seconds - a.transferredAt.seconds);
      if (lastTransferred.length > 0) {
        return dayjs.unix(lastTransferred[0].transferredAt.seconds).tz(timezone).format('MMMM D, YYYY [at] h:mma');
      }
    }
    return null;
  }, [kinderSystemsData, timezone]);

  const transferAttendance = useCallback(() => {
    setIsOpen(false);
    handleTransferAttendance();
  }, [handleTransferAttendance, setIsOpen]);

  useEffect(() => {
    const transferredAt = kinderSystemsData?.transferredAt
      ? dayjs.unix(kinderSystemsData.transferredAt.seconds).tz(timezone).format('MMMM D, YYYY [at] h:mma')
      : attendance?.checkOutFullDate
        ? attendance?.checkOutFullDate.tz(attendance?.timezone).format('MMMM D, YYYY [at] h:mma')
        : null;
    setTransferredDate(transferredAt);
    setLastTransferredDate(getLastTransferredDate());
  }, [kinderSystemsData, timezone, getLastTransferredDate]);

  const getDescription = (): string => {
    if (missingAutomatedCheckOutCorrection) {
      return t('apps.kinderConnect.transferDetails.missingInfo', {
        studentName: attendance?.studentName,
      });
    }

    switch (kinderSystemsData?.status) {
      case KINDERSYSTEMS_REPORTS_STATUS.TRANSFERRED:
        return t('apps.kinderConnect.transferDetails.transferred', { lastTransferredDate });
      case KINDERSYSTEMS_REPORTS_STATUS.ERROR:
        return t('apps.kinderConnect.transferDetails.error');
      default:
        return t('apps.kinderConnect.transferDetails.outOfSync');
    }
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose} extraClasses="text-gray-800">
      <Dialog.Panel>
        <Dialog.Title>{t('Details')}</Dialog.Title>
        <Divider />
        <>
          <Dialog.Description>
            <div className={`flex flex-row items-center gap-2 ${!transferredDate ? 'justify-end' : ''}`}>
              {transferredDate && <strong className="basis-2/3">{transferredDate}</strong>}
              <KinderConnectAttendanceEventStatus
                status={kinderSystemsData?.status}
                missingAutomatedCheckOutCorrection={missingAutomatedCheckOutCorrection}
              />
            </div>
          </Dialog.Description>
          <Dialog.Description>{getDescription()}</Dialog.Description>
          {kinderSystemsData?.status === KINDERSYSTEMS_REPORTS_STATUS.ERROR &&
            kinderSystemsData?.statusReason &&
            !missingAutomatedCheckOutCorrection && (
              <Dialog.Description>
                <strong>
                  {t('apps.kinderConnect.transferDetails.reason', {
                    reason: kinderSystemsData.statusReasonCode
                      ? t(`apps.kinderConnect.transferDetails.skipReasons.${kinderSystemsData.statusReasonCode}`, {
                          ...(kinderSystemsData.statusReasonParams ?? {}),
                          defaultValue: kinderSystemsData.statusReason,
                        })
                      : kinderSystemsData.statusReason,
                  })}
                </strong>
              </Dialog.Description>
            )}
        </>
      </Dialog.Panel>
      <Dialog.Actions>
        {kinderSystemsData?.status !== KINDERSYSTEMS_REPORTS_STATUS.TRANSFERRED &&
          !missingAutomatedCheckOutCorrection && (
            <Button
              primary
              label={lastTransferredDate ? t('common.retryTransfer') : t('common.startTransfer')}
              onClick={transferAttendance}
              extraClasses="w-full ml-4"
              loading={isTransferringAttendance}
            />
          )}
        <Button label={t('common.close')} onClick={onClose} extraClasses="w-full" />
      </Dialog.Actions>
    </Dialog>
  );
};
